"use client";

import clsx from "clsx";
import Image from "next/image";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { getLetterAvatar } from "@/utils/integrations/integration-logos";
import { isEuropeRegion } from "@/utils/common/region";

const HUB_LOGOS = [
  {
    name: "Zapier",
    logo: "https://cdn.simpleicons.org/zapier/FF4A00",
    size: "md" as const,
    top: "3%",
    left: "25%",
    delay: 0,
    svgX: 28,
    svgY: 8,
  },
  {
    name: "Salesforce",
    logo: "/icons/integrations/salesforce.svg",
    size: "lg" as const,
    top: "5%",
    left: "75%",
    delay: 0.2,
    svgX: 78,
    svgY: 10,
  },
  {
    name: "Vertafore",
    logo: "/icons/integrations/vertafore.svg",
    size: "md" as const,
    top: "35%",
    left: "5%",
    delay: 0.4,
    svgX: 8,
    svgY: 38,
  },
  {
    name: "Slack",
    logo: "/icons/integrations/slack.svg",
    size: "md" as const,
    top: "30%",
    right: "2%",
    delay: 0.6,
    svgX: 95,
    svgY: 34,
  },
  {
    name: "HubSpot",
    logo: "https://cdn.simpleicons.org/hubspot/FF7A59",
    size: "lg" as const,
    top: "65%",
    right: "5%",
    delay: 0.8,
    svgX: 92,
    svgY: 68,
  },
  {
    name: "Calendly",
    logo: "https://cdn.simpleicons.org/calendly/006BFF",
    size: "md" as const,
    top: "75%",
    left: "15%",
    delay: 1,
    svgX: 18,
    svgY: 78,
  },
  {
    name: "Mailchimp",
    logo: "https://cdn.simpleicons.org/mailchimp/FFE01B",
    size: "md" as const,
    top: "88%",
    left: "45%",
    delay: 1.2,
    svgX: 48,
    svgY: 90,
  },
];

const EUROPE_HUB_LOGOS = HUB_LOGOS.filter((logo) => logo.name !== "Calendly");

const LOGO_SIZES = {
  lg: { container: "w-16 h-16", image: "w-10 h-10" },
  md: { container: "w-14 h-14", image: "w-8 h-8" },
  sm: { container: "w-11 h-11", image: "w-6 h-6" },
};

const SPARKLES = [
  { id: "sparkle-top-right", top: "12%", left: "55%", size: "text-lg" },
  { id: "sparkle-bottom-right", top: "70%", right: "25%", size: "text-xs" },
  { id: "sparkle-top-left", top: "20%", left: "18%", size: "text-xs" },
  { id: "sparkle-bottom-left", bottom: "15%", left: "35%", size: "text-sm" },
];

const SVG_CENTER_X = 50;
const SVG_CENTER_Y = 50;

function createCurvePath(x: number, y: number): string {
  const midX = (x + SVG_CENTER_X) / 2;
  const midY = (y + SVG_CENTER_Y) / 2;
  const dx = x - SVG_CENTER_X;
  const dy = y - SVG_CENTER_Y;
  const curveOffset = 8;
  const controlX = midX + (dy > 0 ? curveOffset : -curveOffset);
  const controlY = midY + (dx > 0 ? -curveOffset : curveOffset);
  return `M ${x} ${y} Q ${controlX} ${controlY} ${SVG_CENTER_X} ${SVG_CENTER_Y}`;
}

const FloatingLogo = forwardRef<
  HTMLDivElement,
  Readonly<{
    name: string;
    logo: string;
    size: "lg" | "md" | "sm";
    style: React.CSSProperties;
  }>
>(function FloatingLogo({ name, logo, size, style }, ref) {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = LOGO_SIZES[size];

  return (
    <div
      ref={ref}
      className={clsx(
        "absolute",
        sizeClasses.container,
        "flex items-center justify-center",
        "bg-white dark:bg-gray-800 rounded-xl shadow-lg",
        "border border-gray-200 dark:border-gray-700",
        "transition-shadow duration-300 hover:shadow-xl hover:border-gailBlue/40"
      )}
      style={style}
      title={name}
    >
      <Image
        src={imgError ? getLetterAvatar(name) : logo}
        alt={name}
        width={40}
        height={40}
        unoptimized
        className={clsx(sizeClasses.image, "object-contain rounded-lg")}
        onError={() => setImgError(true)}
      />
    </div>
  );
});

function HeroVisualization() {
  const containerRef = useRef<HTMLDivElement>(null);
  const dimsRef = useRef({ width: 600, height: 340 });
  const logoRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const motionPathRefs = useRef<(SVGPathElement | null)[]>([]);
  const isVisibleRef = useRef(true);
  const hubLogos = isEuropeRegion() ? EUROPE_HUB_LOGOS : HUB_LOGOS;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDims = () => {
      const { width, height } = container.getBoundingClientRect();
      dimsRef.current = { width, height };
    };
    updateDims();

    const resizeObserver = new ResizeObserver(updateDims);
    resizeObserver.observe(container);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(container);

    let frameId: number;

    const animate = () => {
      if (isVisibleRef.current) {
        const now = performance.now() / 1000;
        const { width, height } = dimsRef.current;

        hubLogos.forEach((logo, i) => {
          const speed = 0.3 + i * 0.04;
          const phase = i * 1.3;
          const yPx = Math.sin(now * speed + phase) * 6;
          const xPx = Math.cos(now * speed * 0.6 + phase + 0.5) * 3;

          const logoEl = logoRefs.current[i];
          if (logoEl) {
            logoEl.style.transform = `translate(${xPx}px, ${yPx}px)`;
          }

          const svgDx = width > 0 ? (xPx / width) * 100 : 0;
          const svgDy = height > 0 ? (yPx / height) * 100 : 0;
          const newPath = createCurvePath(logo.svgX + svgDx, logo.svgY + svgDy);

          pathRefs.current[i]?.setAttribute("d", newPath);
          motionPathRefs.current[i]?.setAttribute("d", newPath);
        });
      }

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [hubLogos]);

  return (
    <div
      ref={containerRef}
      className="hidden lg:block flex-1 relative"
      style={{ minHeight: "340px" }}
    >
      <div
        className="absolute rounded-full animate-pulse-slow"
        style={{
          top: "50%",
          left: "50%",
          width: "200px",
          height: "200px",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, rgba(0, 98, 226, 0.15) 0%, rgba(0, 98, 226, 0.05) 50%, transparent 70%)",
        }}
      />

      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </linearGradient>
          <filter id="dotGlow">
            <feGaussianBlur stdDeviation="0.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {hubLogos.map((logo, index) => {
          const pathD = createCurvePath(logo.svgX, logo.svgY);
          const pathId = `conn-path-${index}`;
          const duration = 4 + index * 0.7;

          return (
            <g key={logo.name}>
              <path
                ref={(el) => {
                  pathRefs.current[index] = el;
                }}
                d={pathD}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.3"
                strokeDasharray="2 2"
                className="text-gailBlue"
                style={{
                  animation: `fadeIn 0.5s ease-out ${index * 0.1}s both, dashFlow ${6 + index}s linear ${index * 0.1}s infinite`,
                }}
              />
              <path
                ref={(el) => {
                  motionPathRefs.current[index] = el;
                }}
                id={pathId}
                d={pathD}
                fill="none"
                stroke="none"
              />
              <circle r="0.5" className="fill-gailBlue" opacity="0" filter="url(#dotGlow)">
                <animateMotion
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`${index * 0.6}s`}
                >
                  <mpath href={`#${pathId}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;0.55;0.55;0"
                  keyTimes="0;0.15;0.85;1"
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`${index * 0.6}s`}
                />
                <animate
                  attributeName="r"
                  values="0.35;0.6;0.35"
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`${index * 0.6}s`}
                />
              </circle>
            </g>
          );
        })}
      </svg>

      <div
        className="absolute flex items-center justify-center bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-gailBlue/20 p-3"
        style={{
          top: "50%",
          left: "50%",
          width: "100px",
          height: "100px",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 12px 48px rgba(0, 98, 226, 0.25), 0 4px 16px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Image
          src="/icons/gail2-icon-blue.svg"
          alt="Gail"
          width={100}
          height={100}
          className="w-full h-full object-contain"
        />
      </div>

      {hubLogos.map((item, index) => (
        <FloatingLogo
          key={item.name}
          ref={(el) => {
            logoRefs.current[index] = el;
          }}
          name={item.name}
          logo={item.logo}
          size={item.size}
          style={{ top: item.top, left: item.left, right: item.right }}
        />
      ))}

      {SPARKLES.map((sparkle) => (
        <span
          key={sparkle.id}
          className={clsx("absolute text-gailBlue/40 animate-pulse-slow", sparkle.size)}
          style={{
            top: sparkle.top,
            left: sparkle.left,
            right: sparkle.right,
            bottom: sparkle.bottom,
            animationDuration: "2s",
          }}
        >
          ✦
        </span>
      ))}

      <span
        className="absolute text-gray-300 dark:text-gray-600 text-sm font-light"
        style={{ top: "8%", left: "55%" }}
      >
        +
      </span>
      <span
        className="absolute text-gray-300 dark:text-gray-600 text-sm font-light"
        style={{ top: "50%", left: "3%" }}
      >
        +
      </span>
      <span
        className="absolute text-gray-300 dark:text-gray-600 text-sm font-light"
        style={{ bottom: "5%", left: "30%" }}
      >
        +
      </span>
    </div>
  );
}

export default function HeroSection({
  searchQuery,
  onSearchChange,
  onRequestNew,
}: Readonly<{
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRequestNew: () => void;
}>) {
  const { t } = useTranslation("partners");

  return (
    <section
      className={clsx(
        "relative overflow-hidden rounded-2xl",
        "bg-gradient-to-br from-slate-50 via-white to-blue-50/30",
        "dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800",
        "border border-gray-200/80 dark:border-gray-700/80",
        "px-10 py-12 md:px-14 md:py-16"
      )}
      style={{ minHeight: "440px" }}
    >
      <div
        className="absolute inset-0 opacity-[0.02] dark:opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-6">
        <div className="flex-shrink-0 lg:w-[40%] text-center lg:text-left">
          <span className="inline-block px-3.5 py-1.5 mb-5 text-xs font-semibold tracking-wide uppercase rounded-full bg-gailBlue/10 text-gailBlue dark:bg-gailBlue/20">
            {t("marketplaceBadge")}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-gray-900 dark:text-white mb-5 tracking-tight leading-tight">
            {t("marketplaceTitle")}
          </h1>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-4 max-w-lg mx-auto lg:mx-0 leading-relaxed">
            {t("marketplaceSubtitle")}
          </p>
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="relative">
              <Icon
                icon="magnifying-glass"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10 pointer-events-none"
              />
              <input
                type="text"
                placeholder={t("marketplaceSearchPlaceholder")}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className={clsx(
                  "w-full pl-10 pr-10 py-2.5 text-sm rounded-xl",
                  "border border-gray-200 dark:border-gray-700",
                  "bg-white dark:bg-gray-800 shadow-sm hover:shadow-md",
                  "focus:outline-none focus:ring-2 focus:ring-gailBlue/20 focus:border-gailBlue",
                  "transition-all duration-200",
                  "placeholder:text-gray-400 dark:placeholder:text-gray-500"
                )}
              />
              {searchQuery ? (
                <button
                  onClick={() => onSearchChange("")}
                  className={clsx(
                    "absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg",
                    "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                    "hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  )}
                >
                  <Icon icon="xmark" className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t("marketplaceWantMore")}{" "}
              <button
                type="button"
                onClick={onRequestNew}
                className="text-gailBlue hover:underline"
              >
                {t("marketplaceRequestLink")}
              </button>
            </p>
          </div>
        </div>

        <HeroVisualization />

        <div className="lg:hidden flex items-center justify-center py-4">
          <div className="relative">
            <div
              className="absolute rounded-full animate-pulse-slow"
              style={{
                inset: "-24px",
                background: "radial-gradient(circle, rgba(0, 98, 226, 0.1) 0%, transparent 70%)",
              }}
            />
            <div
              className="w-20 h-20 flex items-center justify-center bg-white dark:bg-gray-800 rounded-xl shadow-xl border-2 border-gailBlue/20 p-2"
              style={{ boxShadow: "0 8px 32px rgba(0, 98, 226, 0.2)" }}
            >
              <Image
                src="/icons/gail2-icon-blue.svg"
                alt="Gail"
                width={80}
                height={80}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
